import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
  FaPen,
  FaCalendarAlt,
  FaEnvelope,
  FaFont,
  FaCheck,
  FaExclamation,
  FaLock,
  FaCheckCircle
} from 'react-icons/fa';
import { InputField, Template } from '../types';
import { getFieldColor } from '../documentHelper/DocumentHelperFunctions';

interface InputFieldOverlayProps {
  field: InputField;
  selectedDocument?: Template;
  pdfContainerRef: React.RefObject<HTMLDivElement>;
  handleFieldClick: (field: InputField, mode?: 'sign' | 'type' | React.MouseEvent) => void;
}

const AssignTempInputFieldOverlay: React.FC<InputFieldOverlayProps> = ({
  field,
  selectedDocument,
  pdfContainerRef,
  handleFieldClick
}) => {
  const [containerSize, setContainerSize] = React.useState({ width: 0, height: 0 });
  const [renderTrigger, setRenderTrigger] = React.useState(0);

  // Force re-render on container size changes using ResizeObserver
  React.useEffect(() => {
    if (!pdfContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
        setRenderTrigger((prev) => prev + 1);
      }
    });

    resizeObserver.observe(pdfContainerRef.current);

    // Initial size measurement
    const rect = pdfContainerRef.current.getBoundingClientRect();
    setContainerSize({ width: rect.width, height: rect.height });
    setRenderTrigger(1);

    return () => {
      if (pdfContainerRef.current) {
        resizeObserver.unobserve(pdfContainerRef.current);
      }
    };
  }, [pdfContainerRef]);

  // Force update when field changes
  React.useEffect(() => {
    setRenderTrigger((prev) => prev + 1);
  }, [field.id, field.x, field.y, field.value]);

  if (!pdfContainerRef.current) return null;

  const documentElement = pdfContainerRef.current.querySelector('canvas, img');

  // Calculate positioning and sizing
  let left: number, top: number, width: number, height: number;

  if (documentElement) {
    // Document is loaded (PDF or image)
    const actualDocWidth = documentElement.clientWidth;
    const actualDocHeight = documentElement.clientHeight;

    if (actualDocWidth === 0 || actualDocHeight === 0) return null;

    const isImageDocument = documentElement.tagName.toLowerCase() === 'img';

    if (field.xPercent !== undefined && field.yPercent !== undefined) {
      // Use percentage-based positioning
      left = (field.xPercent / 100) * actualDocWidth;
      top = (field.yPercent / 100) * actualDocHeight;
      if (field.widthPercent !== undefined && field.heightPercent !== undefined) {
        width = (field.widthPercent / 100) * actualDocWidth;
        height = (field.heightPercent / 100) * actualDocHeight;
      } else {
        const defaultWidth = field.type === 'signature' ? 0.18 : 0.12;
        const defaultHeight = field.type === 'signature' ? 0.05 : 0.03;
        width = defaultWidth * actualDocWidth;
        height = defaultHeight * actualDocHeight;
      }
    } else {
      // Use pixel-based positioning
      if (isImageDocument) {
        left = field.x || 0;
        top = field.y || 0;
        const defaultWidth = field.type === 'signature' ? 120 : 80;
        const defaultHeight = field.type === 'signature' ? 35 : 22;
        width = field.width || defaultWidth;
        height = field.height || defaultHeight;
      } else {
        // PDF scaling
        const basePdfWidth = 595;
        const basePdfHeight = 842;
        const scaleX = actualDocWidth / basePdfWidth;
        const scaleY = actualDocHeight / basePdfHeight;
        left = (field.x || 0) * scaleX;
        top = (field.y || 0) * scaleY;
        const defaultWidth = field.type === 'signature' ? 120 : 80;
        const defaultHeight = field.type === 'signature' ? 35 : 22;
        width = (field.width || defaultWidth) * scaleX;
        height = (field.height || defaultHeight) * scaleY;
      }
    }

    // Ensure minimum sizes and boundary constraints
    width = Math.max(width, 100);
    height = Math.max(height, 24);
    left = Math.max(0, Math.min(left, actualDocWidth - width));
    top = Math.max(0, Math.min(top, actualDocHeight - height));
  } else {
    // No document loaded - show tags for empty template
    // Use container dimensions for better positioning
    const containerWidth = containerSize.width || pdfContainerRef.current.clientWidth || 600;
    const containerHeight = containerSize.height || pdfContainerRef.current.clientHeight || 400;

    // Create staggered positioning based on field ID
    const fieldHash = field.id
      ? field.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : Math.random() * 1000;

    const baseLeft = Math.max(20, containerWidth * 0.1);
    const baseTop = Math.max(20, containerHeight * 0.1);

    // Distribute fields in a grid-like pattern
    const fieldIndex = fieldHash % 12; // Max 12 fields in grid
    const cols = Math.min(3, Math.ceil(Math.sqrt(fieldIndex + 1)));
    const rows = Math.ceil((fieldIndex + 1) / cols);

    const colWidth = Math.max(180, (containerWidth - baseLeft * 2) / cols);
    const rowHeight = Math.max(60, (containerHeight - baseTop * 2) / rows);

    const col = fieldIndex % cols;
    const row = Math.floor(fieldIndex / cols);

    left = baseLeft + col * colWidth;
    top = baseTop + row * rowHeight;

    width =
      field.width ||
      (field.type === 'signature' ? Math.min(180, colWidth * 0.8) : Math.min(150, colWidth * 0.7));
    height = field.height || (field.type === 'signature' ? 50 : 30);

    // Ensure tags stay within container bounds
    left = Math.max(10, Math.min(left, containerWidth - width - 10));
    top = Math.max(10, Math.min(top, containerHeight - height - 10));
  }

  const fieldColor = getFieldColor(field.type);
  const isEditable = true;
  const hasValue = Boolean(field.value && field.value.toString().trim() !== '');
  const isRequiredAndEmpty = field.isRequired && field.isEmpEdit && !hasValue;
  const isSignatureField = field.type.toLowerCase() === 'signature';

  const getTooltipText = () => {
    const parts = [field.fieldLabel || field.type];
    if (field.isRequired) parts.push('Required');
    if (isRequiredAndEmpty) parts.push('Needs attention');
    parts.push(isEditable ? 'Click to edit' : 'Read only');
    return parts.join(' • ');
  };

  const borderColor = isRequiredAndEmpty ? '#ef4444' : isEditable ? '#d1d5db' : '#e5e7eb';

  return (
    <Tooltip key={`${field.id}-${renderTrigger}`} title={getTooltipText()} placement="top" arrow>
      <Box
        sx={{
          position: 'absolute',
          left: `${left}px`,
          top: `${top}px`,
          width: `${width}px`,
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isEditable ? 'pointer' : 'default',
          backgroundColor: isEditable
            ? hasValue
              ? 'rgba(255,255,255,0.98)'
              : 'rgba(249,250,251,0.95)'
            : 'rgba(243,244,246,0.9)',
          border: `2px solid ${borderColor}`,
          borderRadius: '6px',
          transition: 'all 0.2s ease-out',
          boxShadow: isRequiredAndEmpty
            ? '0 0 8px rgba(239, 68, 68, 0.3)'
            : '0 2px 4px 0 rgba(0,0,0,0.1)',
          '&:hover': isEditable
            ? {
                backgroundColor: 'rgba(255,255,255,1)',
                boxShadow: '0 4px 8px 0 rgba(0,0,0,0.15)',
                transform: 'translateY(-1px)',
                borderColor: '#3b82f6'
              }
            : {},
          zIndex: documentElement ? 1000 : 100000, // Higher z-index for empty template
          overflow: 'visible',
          padding: '4px 8px'
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (isEditable) handleFieldClick(field, e);
        }}
      >
        {isSignatureField ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.5,
              overflow: 'hidden'
            }}
          >
            {hasValue ? (
              typeof field.value === 'string' && field.value.startsWith('data:image') ? (
                <img
                  src={String(field.value)}
                  alt="Signature"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                />
              ) : field.value === 'checked' ? (
                <FaCheckCircle style={{ color: '#10b981', fontSize: '16px' }} />
              ) : (
                <Typography
                  sx={{
                    fontFamily: '"Brush Script MT", cursive, "Dancing Script", cursive',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#374151',
                    lineHeight: 1.2,
                    letterSpacing: '0.5px'
                  }}
                >
                  {String(field.value)}
                </Typography>
              )
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: '#9ca3af'
                }}
              >
                <FaPen style={{ fontSize: '12px' }} />
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.025em' }}>
                  Sign Here
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: hasValue ? 500 : 400,
                color: hasValue ? '#374151' : '#9ca3af',
                fontStyle: hasValue ? 'normal' : 'italic',
                lineHeight: 1.3,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                width: '100%',
                letterSpacing: '0.025em',
                fontFamily:
                  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
            >
              {hasValue ? String(field.value) : 'Click to edit...'}
            </Typography>
          </Box>
        )}

        {/* Status indicators */}
        {hasValue && isSignatureField && (
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              bgcolor: '#10b981',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              zIndex: 1002
            }}
          >
            <FaCheck style={{ color: 'white', fontSize: '10px' }} />
          </Box>
        )}

        {isRequiredAndEmpty && (
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              bgcolor: '#ef4444',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white',
              boxShadow: '0 2px 6px rgba(239, 68, 68, 0.4)',
              animation: 'pulse 2s infinite',
              zIndex: 1002
            }}
          >
            <FaExclamation style={{ color: 'white', fontSize: '10px' }} />
          </Box>
        )}

        {!isEditable && hasValue && (
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              left: -10,
              bgcolor: '#6b7280',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              zIndex: 1002
            }}
          >
            <FaLock style={{ color: 'white', fontSize: '10px' }} />
          </Box>
        )}

        {/* Field type indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -10,
            left: -10,
            bgcolor: 'white',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #e5e7eb',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1002
          }}
        >
          {field.type === 'signature' && <FaPen style={{ color: '#4f46e5', fontSize: '10px' }} />}
          {(field.type === 'date' || field.type === 'expiry') && (
            <FaCalendarAlt style={{ color: '#6b7280', fontSize: '10px' }} />
          )}
          {field.type === 'email' && <FaEnvelope style={{ color: '#4b5563', fontSize: '10px' }} />}
          {(field.type === 'text' || field.type === 'name' || field.type === 'title') && (
            <FaFont style={{ color: '#374151', fontSize: '10px' }} />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default AssignTempInputFieldOverlay;
